import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout } from '@/components/layout';
import AuthGuard from '@/components/AuthGuard';
import AdminGuard from '@/components/AdminGuard';
import { AdaptiveGuideProvider } from '@/contexts/AdaptiveGuideProvider';
import {
  Dashboard,
  NotFound,
  TemplateLibrary,
  CreateTemplate,
  TemplateDetails,
  EvaluatePerformance,
  EvaluationResults,
  EvaluationsList,
  SelectTemplateForEvaluation,
  LoginPage,
  RegisterPage,
  RequestAccessPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  ChannelPage,
  ChannelManagePage,
  CreateChannelPage,
  AdminPage,
  AdminInvitations,
  AdminUsers,
  CoachInvitations,
  Profile,
  Settings,
  LandingPage,
  AthletesPage,
  AthleteHistoryPage,
  EvaluationsSearchPage,
  AdminMetricsPage,
  WelcomePage,
  ChannelStatsPage,
  ChannelDiscoveryPage,
  ChannelAnalyticsPage,
  VideoLibraryPage,
  PublicSharePage,
  TeamDrillResults,
  ConfirmEmailChangePage,
} from '@/pages';

function App() {
  return (
    <Routes>
      {/*
       * Public marketing landing page — auth-aware root route.
       *
       * Routing approach (R2 decision): minimal blast-radius.
       * `/` is registered as a public route here; LandingPage redirects
       * authenticated users to /templates. Existing authenticated routes
       * remain at their current paths (no /app/* restructure) to keep
       * the diff small and avoid breaking any saved links.
       *
       * Next sprint that restructures authenticated routes under /app/*
       * can move this Navigate target accordingly.
       */}
      <Route path="/" element={<LandingPage />} />

      {/* Auth pages — rendered without sidebar/topbar */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/request-access" element={<RequestAccessPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password/:token" element={<ResetPasswordPage />} />
      {/*
       * Email-change confirmation — public route so the user can click the link
       * from their inbox without needing to be logged in.  The token in the
       * query-string is the credential; no Bearer token required.
       * Format: /confirm-email-change?token={token}
       */}
      <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />

      {/*
       * Public evaluation share page (B-2025-0019) — no auth required.
       * Registered here, outside the AuthGuard wrapper, so unauthenticated
       * visitors can view shared evaluation results without being redirected
       * to the login page. The page fetches data via a plain fetch (no Bearer
       * token) rather than the authed apiClient.
       */}
      <Route path="/share/:token" element={<PublicSharePage />} />

      {/* Protected routes — requires authentication */}
      <Route element={<AuthGuard />}>
        <Route path="/" element={<AppLayout />}>
          <Route index element={<Dashboard />} />

          {/* Template routes */}
          <Route path="templates" element={<TemplateLibrary />} />
          <Route
            path="templates/create"
            element={
              <AdaptiveGuideProvider surface="studio">
                <CreateTemplate />
              </AdaptiveGuideProvider>
            }
          />
          <Route
            path="templates/:id/edit"
            element={
              <AdaptiveGuideProvider surface="studio">
                <CreateTemplate />
              </AdaptiveGuideProvider>
            }
          />
          <Route
            path="templates/:id"
            element={
              <AdaptiveGuideProvider surface="template-details">
                <TemplateDetails />
              </AdaptiveGuideProvider>
            }
          />

          {/* Evaluation routes */}
          {/* B-2026-0024: the old UploadAthlete page used a deprecated hook that
              threw on submit. Redirect to the working submit-and-poll flow at
              /evaluate (the dashboard "Take new evaluation" CTA also targets it). */}
          <Route path="upload-athlete" element={<Navigate to="/evaluate" replace />} />
          <Route path="evaluate" element={<SelectTemplateForEvaluation />} />
          <Route path="evaluate/:templateId" element={<EvaluatePerformance />} />
          <Route path="evaluations" element={<EvaluationsList />} />
          <Route path="evaluations/search" element={<EvaluationsSearchPage />} />
          {/* PTD-10: team-drill leaderboard */}
          <Route path="team-drill/:parentJobId" element={<TeamDrillResults />} />
          <Route path="library" element={<VideoLibraryPage />} />
          <Route
            path="evaluations/:id"
            element={
              <AdaptiveGuideProvider surface="evaluation-results">
                <EvaluationResults />
              </AdaptiveGuideProvider>
            }
          />

          {/* Channel discovery (S21) — canonical at /discover; bare /channels aliases to it.
              react-router v6 ranks static children (channels/create, channels/:slug) above
              the bare channels index, so no shadowing occurs. */}
          <Route path="discover" element={<ChannelDiscoveryPage />} />
          <Route path="channels" element={<Navigate to="/discover" replace />} />

          {/* Channel routes */}
          <Route path="channels/create" element={<CreateChannelPage />} />
          <Route path="channels/:slug" element={<ChannelPage />} />
          <Route path="channels/:slug/manage" element={<ChannelManagePage />} />
          <Route path="channels/:slug/stats" element={<ChannelStatsPage />} />
          <Route path="channels/:slug/analytics" element={<ChannelAnalyticsPage />} />

          {/* Onboarding welcome page — shown once after invitation redemption (B-2026-0078) */}
          <Route path="welcome" element={<WelcomePage />} />

          {/* Profile route */}
          <Route path="profile" element={<Profile />} />

          {/* Settings route */}
          <Route path="settings" element={<Settings />} />

          {/* Admin routes */}
          <Route path="admin" element={<AdminPage />} />
          <Route
            path="admin/invitations"
            element={
              <AdminGuard>
                <AdminInvitations />
              </AdminGuard>
            }
          />
          <Route
            path="admin/users"
            element={
              <AdminGuard>
                <AdminUsers />
              </AdminGuard>
            }
          />
          <Route
            path="admin/metrics"
            element={
              <AdminGuard>
                <AdminMetricsPage />
              </AdminGuard>
            }
          />

          {/* Coach routes */}
          <Route path="coach/invitations" element={<CoachInvitations />} />

          {/* Athlete management routes (coach-visible) */}
          <Route path="athletes" element={<AthletesPage />} />
          <Route path="athletes/:userId" element={<AthleteHistoryPage />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
